import React, { useState, useEffect } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, collection, onSnapshot, deleteDoc, getDoc } from 'firebase/firestore';
import { db, firebaseConfig } from '../servicios/firebase'; 
import BotonVolver from '../components/ui/BotonVolver';
import ModalSeguridad from '../components/ui/ModalSeguridad';

const Usuarios = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [terminalesAgrupadas, setTerminalesAgrupadas] = useState([]); 
  const [loading, setLoading] = useState(true);

  const [nombre, setNombre] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rol, setRol] = useState("admin_sucursal"); 
  const [sucursalAsignada, setSucursalAsignada] = useState(""); 

  const [modalOpen, setModalOpen] = useState(false);
  const [usuarioBorrar, setUsuarioBorrar] = useState(null);

  useEffect(() => {
    const unsubUsers = onSnapshot(collection(db, 'usuarios'), (snap) => {
      setUsuarios(snap.docs.map(d => ({ uid: d.id, ...d.data() })));
      setLoading(false);
    });

    const cargarTerminales = async () => {
      try {
        const snap = await getDoc(doc(db, 'configuraciones', 'terminales'));
        if (snap.exists()) {
          const data = snap.data();
          const grupos = [];
          Object.entries(data).forEach(([zona, contenido]) => {
            if (zona === 'activo') return;
            if (Array.isArray(contenido)) {
              grupos.push({ zona, lista: contenido.sort() });
            }
          });
          grupos.sort((a, b) => a.zona.localeCompare(b.zona));
          setTerminalesAgrupadas(grupos);
        }
      } catch (e) {
        console.error("Error cargando terminales:", e);
      }
    };
    cargarTerminales();

    return () => unsubUsers();
  }, []);

  const crearUsuario = async (e) => {
    e.preventDefault();
    if (!nombre || !email || !password) return alert("Faltan datos");
    // ✅ Solo admin_sucursal requiere sucursal, chofer no
    if (rol === 'admin_sucursal' && !sucursalAsignada) return alert("Debes asignar una sucursal");

    try {
      const secondaryApp = initializeApp(firebaseConfig, "SecondaryApp");
      const secondaryAuth = getAuth(secondaryApp);
      const userCredential = await createUserWithEmailAndPassword(secondaryAuth, email, password);
      const nuevoUid = userCredential.user.uid;

      await setDoc(doc(db, 'usuarios', nuevoUid), {
        nombre,
        email,
        rol,
        // ✅ super_admin → 'global', chofer → 'ninguna', admin_sucursal → su sucursal
        sucursal: rol === 'super_admin' ? 'global' : rol === 'chofer' ? 'ninguna' : sucursalAsignada,
        creadoEn: new Date()
      });

      alert(`✅ Usuario ${nombre} creado con éxito.`);
      setNombre("");
      setEmail("");
      setPassword("");
      setSucursalAsignada("");
    } catch (error) {
      console.error("Error:", error);
      if (error.code === 'auth/email-already-in-use') alert("Ese correo ya existe.");
      else alert("Error: " + error.message);
    }
  };

  const confirmarBorrado = async () => {
    if (!usuarioBorrar) return;
    await deleteDoc(doc(db, 'usuarios', usuarioBorrar.uid));
    setModalOpen(false);
  };

  return (
    <div className="p-6 max-w-6xl mx-auto min-h-screen bg-gray-50">
      <div className="mb-8 bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex flex-wrap justify-between items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Gestión de Equipo</h1>
          <p className="text-gray-500">Crea accesos para tus administradores.</p>
        </div>
        <BotonVolver />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
        {/* FORMULARIO */}
        <div className="lg:col-span-1 bg-white p-6 rounded-2xl shadow-sm border border-gray-200 h-fit">
          <h2 className="font-bold text-gray-700 mb-6 border-b pb-2">👤 Nuevo Usuario</h2>
          <form onSubmit={crearUsuario} className="space-y-4">
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase">Nombre</label>
              <input type="text" className="w-full p-3 border rounded-xl bg-gray-50" value={nombre} onChange={e => setNombre(e.target.value)} placeholder="Ej: Juan Pérez" required />
            </div>
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase">Email</label>
              <input type="email" className="w-full p-3 border rounded-xl bg-gray-50" value={email} onChange={e => setEmail(e.target.value)} placeholder="juan@angeles.com" required />
            </div>
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase">Contraseña</label>
              <input type="password" className="w-full p-3 border rounded-xl bg-gray-50" value={password} onChange={e => setPassword(e.target.value)} placeholder="******" required />
            </div>
            
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase">Rol</label>
              <select className="w-full p-3 border rounded-xl bg-white" value={rol} onChange={e => setRol(e.target.value)}>
                <option value="admin_sucursal">Admin de Sucursal</option>
                <option value="super_admin">Super Admin (Global)</option>
                <option value="chofer">Chofer</option> {/* 👈 NUEVO */}
              </select>
            </div>

            {/* Solo aparece si es admin_sucursal, NO para chofer */}
            {rol === 'admin_sucursal' && (
              <div className="bg-blue-50 p-3 rounded-xl border border-blue-100">
                <label className="text-xs font-bold text-blue-700 uppercase mb-1 block">Asignar a Sucursal</label>
                <select className="w-full p-3 border rounded-lg bg-white font-medium text-gray-700" value={sucursalAsignada} onChange={e => setSucursalAsignada(e.target.value)} required>
                  <option value="">-- Selecciona Terminal --</option>
                  {terminalesAgrupadas.map((grupo) => (
                    <optgroup key={grupo.zona} label={grupo.zona}>
                      {grupo.lista.map((term) => (
                        <option key={term} value={term}>{term}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                <p className="text-[10px] text-blue-500 mt-1">Este usuario solo podrá gestionar salidas de esta terminal.</p>
              </div>
            )}

            {/* Mensaje informativo para chofer */}
            {rol === 'chofer' && (
              <div className="bg-green-50 p-3 rounded-xl border border-green-100">
                <p className="text-xs text-green-700 font-medium">🚌 El chofer solo podrá escanear y validar boletos.</p>
              </div>
            )}

            <button className="w-full bg-blue-600 text-white font-bold py-3 rounded-xl hover:bg-blue-700 shadow-lg mt-4">
              Crear Acceso
            </button>
          </form>
        </div>

        {/* LISTA USUARIOS */}
        <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
          {usuarios.map(u => (
            <div key={u.uid} className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 flex flex-col justify-between">
              <div className="flex justify-between items-start mb-4">
                <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center text-lg">
                  {/* ✅ Ícono según rol */}
                  {u.rol === 'super_admin' ? '👑' : u.rol === 'chofer' ? '🚌' : '👤'}
                </div>
                {u.rol !== 'super_admin' && (
                  <button onClick={() => { setUsuarioBorrar(u); setModalOpen(true); }} className="text-red-300 hover:text-red-500">🗑️</button>
                )}
              </div>
              <div>
                <h3 className="font-bold text-gray-800">{u.nombre}</h3>
                <p className="text-sm text-gray-400">{u.email}</p>
              </div>
              <div className="mt-4 pt-4 border-t border-gray-50 flex justify-between items-center">
                {/* ✅ Badge según rol */}
                <span className={`text-xs font-bold px-2 py-1 rounded ${
                  u.rol === 'super_admin' ? 'bg-purple-100 text-purple-700' : 
                  u.rol === 'chofer'      ? 'bg-green-100 text-green-700' : 
                                           'bg-blue-100 text-blue-700'
                }`}>
                  {u.rol === 'super_admin' ? 'SUPER ADMIN' : u.rol === 'chofer' ? 'CHOFER' : 'ADMIN SUCURSAL'}
                </span>
                {u.sucursal !== 'global' && u.sucursal !== 'ninguna' && (
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded border border-gray-200">
                    📍 {u.sucursal}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <ModalSeguridad isOpen={modalOpen} onClose={() => setModalOpen(false)} onConfirm={confirmarBorrado} title="Revocar Acceso" message={`¿Estás seguro de quitar el acceso a ${usuarioBorrar?.nombre}?`} actionType="danger" />
    </div>
  );
};

export default Usuarios;